import axios from "axios"
import { jwtDecode } from "jwt-decode"
import { URL_PAYROLL, URL_BILLING } from "../utils/urls"
import { extractApiErrorMessage } from "../utils/errors"

// Guarda contra el bucle de recargas por TOTP (403 con código 5400): cuándo se
// recargó por última vez, en sessionStorage para que sobreviva a la recarga.
export const TOTP_RELOAD_KEY = "totp_reload_at"
export const TOTP_RELOAD_WINDOW_MS = 10000

// 403 de negocio que la pantalla explica con el mensaje del backend: la alerta genérica
// «No tiene permisos…» la contradiría. 6656 = TECNICA_STOCK_SIN_LEGAJO (Mi stock sin legajo).
export const CODIGOS_403_SIN_ALERTA = [6656]

const recargoPorTotpHacePoco = () => {
    try {
        const ultima = Number(sessionStorage.getItem(TOTP_RELOAD_KEY))
        return Number.isFinite(ultima) && ultima > 0 && Date.now() - ultima < TOTP_RELOAD_WINDOW_MS
    } catch (_) {
        return false
    }
}

const marcarRecargaPorTotp = () => {
    try {
        sessionStorage.setItem(TOTP_RELOAD_KEY, String(Date.now()))
    } catch (_) { /* sin sessionStorage la guarda no funciona, pero se recarga igual */ }
}

export const axiosInterceptor = () => {
    // Ya se pidió una recarga por 5400 en esta página: los demás 5400 que
    // lleguen antes de que se descargue no hacen nada (ni otra recarga ni aviso).
    let recargandoPorTotp = false;

    // Seleccionar token según el portal actual para evitar que un token de
    // admin se envíe en llamadas del portal de clientes (o viceversa).
    const getPortalToken = () => {
        const path = window.location.pathname;
        if (path.startsWith('/clientes')) return sessionStorage.getItem("cliente_token");
        if (path.startsWith('/proveedores')) return sessionStorage.getItem("proveedor_token");
        if (path.startsWith('/auditor')) return sessionStorage.getItem("auditor_token");
        if (path.startsWith('/platform')) return sessionStorage.getItem("platform_token");
        if (path.startsWith('/portal-facturacion')) return sessionStorage.getItem("facturacion_token");
        if (path.startsWith('/portal-sueldos')) return sessionStorage.getItem("sueldos_token");
        if (path.startsWith('/portal-legales')) return sessionStorage.getItem("legales_token");
        if (path.startsWith('/portal-accesos')) return sessionStorage.getItem("accesos_token");
        return sessionStorage.getItem("access_token");
    };

    const updateHeaders = (request) => {
        const token = getPortalToken();

        if (token) {
            request.headers['Authorization'] = `Bearer ${token}`;
        }

        // Si la petición NO es FormData ni un body binario crudo, forzamos
        // 'Content-Type' a 'application/json'. Con FormData se deja intacto
        // (multipart/form-data con boundary) y con un File/Blob/ArrayBuffer
        // también se deja intacto: es el caso del padrón de ARBA
        // (subirPadronArba), que manda el archivo crudo con
        // Content-Type: application/octet-stream porque el backend lo lee en
        // streaming y no acepta multipart ahí.
        const isFormData = request.data instanceof FormData;
        const isBinaryBody = request.data instanceof Blob || request.data instanceof ArrayBuffer;
        if (!isFormData && !isBinaryBody) {
            request.headers['Content-Type'] = 'application/json';
        }

        // TOTP session token para endpoints protegidos
        const totpSession = sessionStorage.getItem("totp_session");
        if (totpSession) {
            request.headers['X-Totp-Session'] = totpSession;
        }

        // Inyectar CUIT del tenant para requests al Payroll Engine y Billing API.
        // Debe salir del MISMO token que viaja en Authorization: el back compara
        // el claim 'cuit' del bearer contra este header y rechaza con 400 si no
        // coinciden (o si el header falta).
        if (request.url?.startsWith(URL_PAYROLL) || request.url?.startsWith(URL_BILLING)) {
            if (token) {
                try {
                    const decoded = jwtDecode(token);
                    if (decoded.cuit) {
                        request.headers['X-Cuit-Representada'] = decoded.cuit;
                    }
                } catch (_) { /* JWT inválido — el back rechazará el request */ }
            }
        }

        return request;
    };

    axios.interceptors.request.use((req) => {
        // No modificamos el header en la ruta de login
        if (req.url?.includes('login')) return req;

        return updateHeaders(req);
    });

    axios.interceptors.response.use(
        (response) => response,
        (error) => {
            const status = error.response?.status;
            const code = error.response?.data?.code;
            const translatedMessage = extractApiErrorMessage(error, "Ocurrió un error.");

            if (error.response?.data) {
                error.response.data.message = translatedMessage;
                error.response.data.detail = error.response.data.detail || translatedMessage;
                error.response.data.title = error.response.data.title || translatedMessage;
            }

            // TOTP error (5400): limpiar sesión TOTP y recargar para que TotpGate
            // vuelva a pedir el código (flujo normal de /haberes con la sesión vencida).
            // Guarda anti-bucle: si ya se recargó por 5400 hace menos de 10 s y vuelve
            // a pasar, la recarga no lo arregla (p. ej. una pantalla sin TotpGate que
            // llama a un endpoint protegido): no se recarga y sigue como cualquier 403,
            // con el aviso de abajo.
            if (status === 403 && code === 5400) {
                sessionStorage.removeItem("totp_session");
                if (recargandoPorTotp) return Promise.reject(error);
                if (!recargoPorTotpHacePoco()) {
                    recargandoPorTotp = true;
                    marcarRecargaPorTotp();
                    window.location.reload();
                    return Promise.reject(error);
                }
                console.warn("403 5400 (TOTP) repetido tras recargar: no se vuelve a recargar", error.config?.url);
            }

            // 401 = token inválido/expirado → cerrar sesión y redirigir
            // Ignorar 401 del endpoint de login (credenciales incorrectas se manejan en Login.jsx)
            if (status === 401 && !error.config?.url?.includes('login')) {
                const isCliente = !!sessionStorage.getItem("cliente_token");
                const isProveedor = !!sessionStorage.getItem("proveedor_token");
                const isAuditor = !!sessionStorage.getItem("auditor_token");
                const isPlatform = !!sessionStorage.getItem("platform_token");
                const isFacturacion = !!sessionStorage.getItem("facturacion_token");
                const isSueldos = !!sessionStorage.getItem("sueldos_token");
                // Legales comparte el token con la app (puente desde access_token), así que
                // se decide por la ruta: solo vuelve a su login si el 401 fue dentro del portal.
                const isLegales = !!sessionStorage.getItem("legales_token")
                    && window.location.pathname.startsWith('/portal-legales');
                // Accesos también comparte el token con la app (puente desde access_token).
                const isAccesos = !!sessionStorage.getItem("accesos_token")
                    && window.location.pathname.startsWith('/portal-accesos');
                sessionStorage.removeItem("access_token");
                sessionStorage.removeItem("cliente_token");
                sessionStorage.removeItem("cliente_data");
                sessionStorage.removeItem("proveedor_token");
                sessionStorage.removeItem("proveedor_data");
                sessionStorage.removeItem("auditor_token");
                sessionStorage.removeItem("auditor_data");
                sessionStorage.removeItem("platform_token");
                sessionStorage.removeItem("platform_data");
                sessionStorage.removeItem("facturacion_token");
                sessionStorage.removeItem("facturacion_data");
                sessionStorage.removeItem("sueldos_token");
                sessionStorage.removeItem("sueldos_data");
                sessionStorage.removeItem("legales_token");
                sessionStorage.removeItem("legales_data");
                sessionStorage.removeItem("accesos_token");
                sessionStorage.removeItem("accesos_data");
                sessionStorage.removeItem("totp_session");
                window.location.href = isAccesos ? "/portal-accesos" : isLegales ? "/portal-legales" : isPlatform ? "/platform" : isAuditor ? "/auditor" : isSueldos ? "/portal-sueldos" : isFacturacion ? "/portal-facturacion" : isProveedor ? "/proveedores" : (isCliente ? "/clientes" : "/");
            }

            // 403 = sin permiso para este recurso → mostrar alerta, NO cerrar sesión
            if (status === 403 && !CODIGOS_403_SIN_ALERTA.includes(code)) {
                window.dispatchEvent(new CustomEvent('axios-forbidden', {
                    detail: { url: error.config?.url || '' }
                }));
            }

            return Promise.reject(error);
        }
    );
};
